import { usePermissions, UserPermission, WriteOn, DeleteOn } from 'modules/permissions';
import React, { useCallback } from 'react';
import { ObservationStatusEnum } from 'shared/enums/ObservationStatus.enum';
import { StyledPanelGrid, StyledPanelRow, StyledPanelColumn, StyledContainer } from 'shared/styled/panel.stayled';
import { Opportunity } from '../../../queries/view-opportunity.queries.types';
import { ObservationFileEntity } from 'shared/types/file/ObservationFileEntity';
import { IRemoveFiles } from 'shared/utils/files/types/IRemoveFiles';
import { ObservationCategoryEntity } from 'shared/types/domain/ObservationCategoryEntity';
import OpportunityReporter from '../../OpportunityReporter';
import AmendObservationPhotos from 'components/ObservationPhoto/components/AmendObservationPhotos';
import OpportunityCategorisation from 'components/OpportunityCategorisation';
import OpportunityDetails from 'components/OpportunityDetails';

interface AmmendOpportunityForm {
   opportunity: Opportunity | undefined;
   removeFiles: IRemoveFiles<ObservationFileEntity>;
   files: ObservationFileEntity[];
}

const AmmendOpportunityForm = ({ opportunity, removeFiles, files }: AmmendOpportunityForm) => {
   const { hasPermission } = usePermissions();

   const canEditOpportunity = useCallback((): boolean => {
      return (
          opportunity?.observationStatus === ObservationStatusEnum.Open &&
         hasPermission(WriteOn(UserPermission.ExistingObservation))
      );
   }, [opportunity, hasPermission]);

   const canDeletePhoto = useCallback((): boolean => {
      return (
          opportunity?.observationStatus === ObservationStatusEnum.Open && hasPermission(DeleteOn(UserPermission.ImageFile))
      );
   }, [opportunity, hasPermission]);

   const canEditReporter = useCallback((): boolean => {
      return (
         opportunity?.observationStatus === ObservationStatusEnum.Open &&
         hasPermission(WriteOn(UserPermission.ObservationReporter))
      );
   }, [opportunity, hasPermission]);

   const selectedCategory = useCallback((): ObservationCategoryEntity | undefined => {
      if (opportunity) {
          return {
            observationCategoryId: opportunity.observationCategoryId,
            observationCategoryName: opportunity.observationCategoryName,
            observationCategoryNameDe: opportunity.observationCategoryName,
            processId: opportunity.processId,
            reachId: opportunity.reachId
         };
      }

      return undefined;
   }, [opportunity]);

   return (
      <React.Fragment>
         <StyledContainer>
            <StyledPanelGrid fluid>
               <StyledPanelRow>
                  <StyledPanelColumn md={12} lg={4} xl={4}>
                     <OpportunityCategorisation readOnly={!canEditOpportunity()} selectedCategory={selectedCategory()} />
                  </StyledPanelColumn>
                  <StyledPanelColumn md={12} lg={4} xl={4}>
                     <OpportunityDetails readOnly={!canEditOpportunity()} />
                     <AmendObservationPhotos
                        uniqueRecordId={opportunity?.uniqueRecordId}
                        observationType={'Opportunity'}
                        removeFiles={removeFiles}
                        files={files}
                        canDelete={canDeletePhoto()}
                        useEnglish
                     />
                  </StyledPanelColumn>
                  <StyledPanelColumn md={12} lg={4} xl={4}>
                     <OpportunityReporter nearMissReadOnly={true} reporterReadOnly={!canEditReporter()} />
                  </StyledPanelColumn>
               </StyledPanelRow>
            </StyledPanelGrid>
         </StyledContainer>
      </React.Fragment>
   );
};

export default AmmendOpportunityForm;
